import { useState, useRef } from 'react';
import { testimonials, siteInfo, articles, categories } from '@/mocks/data';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

const CONTACT_FORM_URL = 'https://piccraft.github.io/api/form/d9u54stca23r0tr1aqt0';

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="关于我们"
        description={`了解${siteInfo.name}——${siteInfo.description}我们成立于${siteInfo.founded}年，已发布${siteInfo.articleCount}篇摄影教程，服务${siteInfo.readerCount}读者。`}
        keywords="拍得好,摄影教程平台,关于我们,摄影社区"
        canonicalUrl="/about"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: `关于${siteInfo.name}`,
          description: siteInfo.description,
        }}
      />

      <main className="min-h-screen pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          <AboutIntro />
          <AboutStats />
          <AboutTestimonials />
          <AboutContact />
        </div>
      </main>
    </>
  );
}

function AboutIntro() {
  return (
    <section className="mb-16">
      <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading mb-6">
        关于拍得好
      </h1>
      <div className="prose-content max-w-none">
        <p className="text-foreground-700 leading-loose mb-4">
          拍得好成立于{siteInfo.founded}年，是一个专注于<strong>自拍与人像抓拍技巧</strong>的摄影教程平台。在这个人人都能用手机拍出好照片的时代，我们相信好的摄影技术不应该被专业门槛所限制。
        </p>
        <p className="text-foreground-700 leading-loose mb-4">
          无论你是刚拿起手机想要拍出更好自拍的新手，还是已经有一定基础想要进阶的摄影爱好者，都能在这里找到适合你的内容。我们的教程涵盖自拍构图、街头抓拍技巧、设备选购指南、拍摄角度选择、后期处理方法和人文纪实摄影等多个领域。
        </p>
        <p className="text-foreground-700 leading-loose mb-4">
          每一篇文章都由经验丰富的摄影师精心撰写，配有详细的实操步骤和实例分析。我们坚持"实用至上"原则，不堆砌专业术语，不推荐昂贵器材，只讲真正能帮到你提升摄影水平的内容。
        </p>
        <h2 className="text-xl font-bold text-foreground-950 font-heading mt-8 mb-4">
          我们的理念
        </h2>
        <p className="text-foreground-700 leading-loose mb-4">
          我们认为摄影的本质是<strong>记录和表达</strong>。自拍不是自恋，而是自我认知和表达的方式；抓拍不是偷窥，而是捕捉真实生活的艺术。无论用什么设备，只要你能通过镜头讲述故事、传递情感，就是好的摄影作品。
        </p>
        <p className="text-foreground-700 leading-loose">
          在拍得好，我们不仅教你怎么拍，更希望帮你找到属于自己的摄影语言。技术是手段，审美和观察力才是核心。
        </p>
      </div>
    </section>
  );
}

function AboutStats() {
  return (
    <section className="mb-16 py-10 bg-background-100 rounded-2xl">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        <div>
          <div className="text-3xl font-bold text-primary-500 font-heading mb-1">{siteInfo.articleCount}</div>
          <div className="text-xs text-foreground-500">原创教程</div>
        </div>
        <div>
          <div className="text-3xl font-bold text-accent-500 font-heading mb-1">{siteInfo.readerCount}</div>
          <div className="text-xs text-foreground-500">忠实读者</div>
        </div>
        <div>
          <div className="text-3xl font-bold text-secondary-600 font-heading mb-1">{categories.length}</div>
          <div className="text-xs text-foreground-500">内容分类</div>
        </div>
        <div>
          <div className="text-3xl font-bold text-primary-500 font-heading mb-1">{siteInfo.founded}</div>
          <div className="text-xs text-foreground-500">成立年份</div>
        </div>
      </div>
    </section>
  );
}

function AboutTestimonials() {
  return (
    <section className="mb-16">
      <h2 className="text-2xl font-bold text-foreground-950 font-heading mb-8 text-center">
        读者口碑
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {testimonials.map((t) => (
          <div
            key={t.id}
            className="p-5 bg-background-50 border border-background-200/70 rounded-xl"
          >
            <div className="flex items-center gap-1 mb-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <i
                  key={i}
                  className={`text-sm ${i < t.rating ? 'text-yellow-500 ri-star-fill' : 'text-foreground-300 ri-star-line'}`}
                ></i>
              ))}
            </div>
            <p className="text-sm text-foreground-700 leading-relaxed mb-4">
              &ldquo;{t.content}&rdquo;
            </p>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100">
                <i className={`${t.avatar} text-sm text-secondary-600`}></i>
              </div>
              <div>
                <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                <div className="text-xs text-foreground-500">{t.role}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function AboutContact() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [msg, setMsg] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = formRef.current;
    if (!form) return;

    const fd = new FormData(form);
    const honeypot = fd.get('phone_alt');
    if (honeypot && (honeypot as string).trim() !== '') {
      setStatus('success');
      setMsg('消息已发送！我们会尽快回复你。');
      setFormData({ name: '', email: '', message: '' });
      return;
    }

    if (formData.message.length > 500) {
      setStatus('error');
      setMsg('留言内容不能超过500个字符。');
      return;
    }

    setStatus('loading');

    try {
      const payload = new URLSearchParams();
      payload.append('name', formData.name.trim());
      payload.append('email', formData.email.trim());
      payload.append('message', formData.message.trim());

      const resp = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await resp.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore
      }

      const code = (parsed as { code?: string }).code;
      const serverMsg =
        ((parsed as { meta?: { message?: string } }).meta?.message) ||
        ((parsed as { message?: string }).message) ||
        '';

      if (resp.ok && code === 'OK') {
        setStatus('success');
        setMsg('消息已发送！我们会尽快回复你。');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setStatus('error');
        setMsg(serverMsg || '发送失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setMsg('网络错误，请检查网络连接后重试。');
    }
  };

  return (
    <section>
      <h2 className="text-2xl font-bold text-foreground-950 font-heading mb-8 text-center">
        联系我们
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <div>
          <p className="text-foreground-700 leading-relaxed mb-6">
            有任何问题、建议或合作意向？欢迎通过以下方式联系我们，或直接填写右侧表单发送消息。
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                <i className="ri-mail-line text-lg text-primary-600"></i>
              </div>
              <div>
                <div className="text-xs text-foreground-500">邮箱</div>
                <div className="text-sm font-medium text-foreground-900">{siteInfo.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100">
                <i className="ri-wechat-line text-lg text-accent-600"></i>
              </div>
              <div>
                <div className="text-xs text-foreground-500">微信</div>
                <div className="text-sm font-medium text-foreground-900">{siteInfo.wechat}</div>
              </div>
            </div>
          </div>
        </div>

        <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="" className="space-y-4">
          <input
            type="text"
            name="phone_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="honeypot-field"
          />
          <div>
            <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
              姓名
            </label>
            <input
              id="contact-name"
              name="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
              placeholder="你的名字"
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
              邮箱
            </label>
            <input
              id="contact-email"
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
              placeholder="your@email.com"
            />
          </div>
          <div>
            <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
              留言
              <span className="text-foreground-400 font-normal ml-1">（不超过500字）</span>
            </label>
            <textarea
              id="contact-message"
              name="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              maxLength={500}
              rows={4}
              className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
              placeholder="请输入你想说的话..."
            ></textarea>
            <div className="text-xs text-foreground-400 mt-1 text-right">
              {formData.message.length}/500
            </div>
          </div>
          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full px-6 py-3 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
          >
            {status === 'loading' ? '发送中...' : '发送消息'}
          </button>
          {status === 'success' && (
            <p className="text-sm text-green-600 text-center">{msg}</p>
          )}
          {status === 'error' && (
            <p className="text-sm text-red-500 text-center">{msg}</p>
          )}
        </form>
      </div>
    </section>
  );
}

